import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import TrainingHostLobbyContent from '../../components/training/TrainingHostLobbyContent';
import {
  TEACHER_TRAINING_BASE,
  TEACHER_TRAINING_LOBBY_PATH,
  canManageOwnContent,
  teacherTrainingProgramPath,
} from '../../config/contentRoutes';
import {
  getTrainingHostBySlug,
  subscribeTrainingProgramsByHost,
} from '../../utils/trainingHost';
import {
  importMaruLegacyTrainingPrograms,
  syncLobbyAutoLegacyPrograms,
} from '../../utils/importLegacyTrainingPrograms';
import { FEATURE_TRAINING_LEGACY_AUTO_SYNC } from '../../config/featureFlags';
import type { TrainingHost, TrainingProgram } from '../../types/trainingHost';

const TrainingHostPublicPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canManage = canManageOwnContent(currentUser?.role);

  const [host, setHost] = useState<TrainingHost | null>(null);
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isOwner = !!host && !!currentUser && host.ownerUid === currentUser.uid;

  useEffect(() => {
    let unsubPrograms: (() => void) | undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const hostData = await getTrainingHostBySlug(slug, currentUser?.uid);
        if (cancelled) return;

        if (!hostData) {
          setHost(null);
          setNotFound(true);
          setLoading(false);
          return;
        }

        const isOwn = currentUser?.uid === hostData.ownerUid;
        if (!hostData.isPublic && !isOwn) {
          setHost(null);
          setNotFound(true);
          setLoading(false);
          return;
        }

        setHost(hostData);
        unsubPrograms = subscribeTrainingProgramsByHost(
          hostData.id,
          { includePrivate: isOwn },
          (items) => {
            if (!cancelled) {
              setPrograms(isOwn ? items : items.filter((p) => p.isPublic));
              setLoading(false);
            }
          },
          (err) => {
            console.error(err);
            if (!cancelled) setLoading(false);
          }
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    };

    if (slug) load();
    return () => {
      cancelled = true;
      unsubPrograms?.();
    };
  }, [slug, currentUser?.uid]);

  useEffect(() => {
    if (!FEATURE_TRAINING_LEGACY_AUTO_SYNC) return;
    if (!host || !currentUser?.uid || host.ownerUid !== currentUser.uid) return;

    void importMaruLegacyTrainingPrograms(currentUser.uid, host.id).catch((err) => {
      console.warn('레거시 연수 동기화 실패:', err);
    });
    void syncLobbyAutoLegacyPrograms(currentUser.uid, host.id).catch((err) => {
      console.warn('로비 자동 연수 동기화 실패:', err);
    });
  }, [host?.id, host?.ownerUid, currentUser?.uid]);

  const openProgram = (program: TrainingProgram) => {
    if ((program.contentType === 'editor' || program.contentHtml) && program.programSlug) {
      navigate(teacherTrainingProgramPath(host!.publicSlug, program.programSlug));
      return;
    }
    if (!program.path) return;
    if (program.linkType === 'external') {
      window.open(program.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(program.path);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</p>
      </div>
    );
  }

  if (notFound || !host) {
    return (
      <div
        style={{
          padding: '24px',
          maxWidth: '640px',
          margin: '0 auto',
          fontFamily: 'var(--font-family-primary)',
        }}
      >
        <h1 style={{ fontSize: '22px', color: 'var(--Color-text-strong)' }}>연수 허브를 찾을 수 없습니다</h1>
        <p style={{ color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
          주소가 잘못되었거나 비공개 허브일 수 있습니다.
        </p>
        <Button variant="outline" size="medium" onClick={() => navigate(TEACHER_TRAINING_BASE)}>
          허브 목록으로
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <TrainingHostLobbyContent
        mode="view"
        photoURL={host.photoURL}
        profile={{
          displayName: host.displayName,
          schoolName: host.schoolName ?? '',
          bio: host.bio ?? '',
          publicSlug: host.publicSlug,
        }}
        programs={programs}
        showPrivatePrograms={isOwner}
        publicPath={`/teacher-training/h/${host.publicSlug}`}
        onOpenProgram={openProgram}
        headerActions={
          <>
            {isOwner && canManage && (
              <Button variant="primary" size="medium" onClick={() => navigate(TEACHER_TRAINING_LOBBY_PATH)}>
                로비 편집
              </Button>
            )}
            <Button variant="outline" size="medium" onClick={() => navigate(TEACHER_TRAINING_BASE)}>
              전체 허브
            </Button>
          </>
        }
      />
    </div>
  );
};

export default TrainingHostPublicPage;
